(function () {
  'use strict';

  if (window.SawseeCollectionColorSlider) return;
  window.SawseeCollectionColorSlider = true;

  const SELECTORS = {
    slider: '[data-color-slider]',
    slide: '[data-color-slide]',
    track: '[data-color-track]',
    dot: '[data-color-dot]',
    previous: '[data-color-slider-previous]',
    next: '[data-color-slider-next]',
    swatch: '.product-card-swatch',
    imageLink: '[data-color-slider-link]',
    productLink: '[data-color-slider-product-link]'
  };

  const dragStates = new WeakMap();
  const loopResetTimers = new WeakMap();
  const prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getSwatchColor(swatch) {
    if (swatch.dataset.colorHandle) return swatch.dataset.colorHandle;

    const label = swatch.querySelector('.visually-hidden, span');
    return slugify(label ? label.textContent : '');
  }

  function getGroupSlides(figure, color) {
    return Array.from(figure.querySelectorAll(SELECTORS.slide)).filter(function (slide) {
      return slide.dataset.colorGroup === color;
    });
  }

  function getGroupDots(figure, color) {
    return Array.from(figure.querySelectorAll(SELECTORS.dot)).filter(function (dot) {
      return dot.dataset.colorGroup === color;
    });
  }

  function getTrack(figure, color) {
    return Array.from(figure.querySelectorAll(SELECTORS.track)).find(function (track) {
      return track.dataset.colorGroup === color;
    }) || null;
  }

  function prepareTracks(figure) {
    if (figure.querySelector(SELECTORS.track)) return;

    const link = figure.querySelector(SELECTORS.imageLink);
    if (!link) return;

    const slides = Array.from(link.querySelectorAll(':scope > ' + SELECTORS.slide));
    const groups = new Map();

    slides.forEach(function (slide) {
      const color = slide.dataset.colorGroup || '';
      if (!groups.has(color)) groups.set(color, []);
      groups.get(color).push(slide);
    });

    groups.forEach(function (groupSlides, color) {
      const track = document.createElement('div');
      track.className = 'color-slider-track';
      track.dataset.colorTrack = '';
      track.dataset.colorGroup = color;
      track.setAttribute('aria-live', 'polite');

      if (groupSlides.length > 1) {
        const lastClone = groupSlides[groupSlides.length - 1].cloneNode(true);
        lastClone.classList.remove('is-active');
        lastClone.classList.add('color-slider-clone');
        lastClone.removeAttribute('data-color-slide');
        lastClone.dataset.colorClone = 'last';
        lastClone.setAttribute('aria-hidden', 'true');
        lastClone.querySelectorAll('[id]').forEach(function (element) {
          element.removeAttribute('id');
        });
        track.appendChild(lastClone);
      }

      groupSlides.forEach(function (slide) {
        track.appendChild(slide);
      });

      if (groupSlides.length > 1) {
        const firstClone = groupSlides[0].cloneNode(true);
        firstClone.classList.remove('is-active');
        firstClone.classList.add('color-slider-clone');
        firstClone.removeAttribute('data-color-slide');
        firstClone.dataset.colorClone = 'first';
        firstClone.setAttribute('aria-hidden', 'true');
        firstClone.querySelectorAll('[id]').forEach(function (element) {
          element.removeAttribute('id');
        });
        track.appendChild(firstClone);
      }

      link.appendChild(track);
    });
  }

  function unveilSlide(slide) {
    if (!slide) return;

    slide.querySelectorAll('img, source').forEach(function (media) {
      if (media.dataset.srcset && !media.getAttribute('srcset')) {
        media.setAttribute('srcset', media.dataset.srcset);
      }

      if (media.dataset.src && !media.getAttribute('src')) {
        media.setAttribute('src', media.dataset.src);
      }

      if (media.tagName === 'IMG' && window.lazySizes && window.lazySizes.loader) {
        window.lazySizes.loader.unveil(media);
      }
    });
  }

  function preloadGroup(figure, color) {
    getGroupSlides(figure, color).forEach(unveilSlide);
  }

  function preloadNearbySlides(figure, color, index) {
    const slides = getGroupSlides(figure, color);
    if (!slides.length) return;

    unveilSlide(slides[index]);
    unveilSlide(slides[index + 1]);
    unveilSlide(slides[index - 1]);

    const track = getTrack(figure, color);
    if (track && index === 0) {
      unveilSlide(track.querySelector('[data-color-clone="last"]'));
    }
    if (track && index === slides.length - 1) {
      unveilSlide(track.querySelector('[data-color-clone="first"]'));
    }
  }

  function getMotionDuration() {
    return prefersReducedMotion.matches ? 0 : 340;
  }

  function positionTrack(track, index, animate, offsetPixels) {
    if (!track) return;

    const duration = getMotionDuration();
    const cloneOffset = track.querySelector('[data-color-clone]') ? 1 : 0;
    track.style.transition = animate
      ? 'transform ' + duration + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)'
      : 'none';
    track.style.transform = 'translate3d(calc(' + (-(index + cloneOffset) * 100) + '% + ' + (offsetPixels || 0) + 'px), 0, 0)';
  }

  function setSlide(figure, requestedIndex, options) {
    const settings = options || {};
    const color = figure.dataset.activeColor;
    const slides = getGroupSlides(figure, color);
    if (!slides.length) return;

    const dots = getGroupDots(figure, color);
    const shouldWrap = settings.wrap !== false;
    let index = requestedIndex;
    const crossesStart = shouldWrap && requestedIndex < 0;
    const crossesEnd = shouldWrap && requestedIndex >= slides.length;

    if (shouldWrap) {
      index = (index + slides.length) % slides.length;
    } else {
      index = Math.max(0, Math.min(index, slides.length - 1));
    }

    figure.querySelectorAll(SELECTORS.track).forEach(function (track) {
      const activeTrack = track.dataset.colorGroup === color;
      track.style.display = activeTrack ? 'flex' : 'none';
      track.setAttribute('aria-hidden', activeTrack ? 'false' : 'true');
    });

    const track = getTrack(figure, color);
    const animatedTrackIndex = crossesStart || crossesEnd ? requestedIndex : index;
    positionTrack(track, animatedTrackIndex, settings.animate !== false, 0);

    const previousLoopTimer = loopResetTimers.get(figure);
    if (previousLoopTimer) window.clearTimeout(previousLoopTimer);

    if (crossesStart || crossesEnd) {
      figure.dataset.colorSliderLooping = 'true';
      const resetTimer = window.setTimeout(function () {
        if (figure.dataset.activeColor !== color) return;
        positionTrack(track, index, false, 0);
        delete figure.dataset.colorSliderLooping;
        loopResetTimers.delete(figure);
      }, getMotionDuration() + 30);
      loopResetTimers.set(figure, resetTimer);
    } else {
      delete figure.dataset.colorSliderLooping;
    }

    figure.querySelectorAll(SELECTORS.slide).forEach(function (slide) {
      const active = slide.dataset.colorGroup === color && slide === slides[index];
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    figure.querySelectorAll(SELECTORS.dot).forEach(function (dot) {
      const belongsToColor = dot.dataset.colorGroup === color;
      dot.style.display = belongsToColor ? '' : 'none';
      dot.classList.toggle('is-active', belongsToColor && dot === dots[index]);
    });

    figure.dataset.activeIndex = String(index);
    figure.classList.remove('is-dragging');
    preloadNearbySlides(figure, color, index);

    const hasMultipleImages = slides.length > 1;
    const previousButton = figure.querySelector(SELECTORS.previous);
    const nextButton = figure.querySelector(SELECTORS.next);

    if (previousButton) {
      previousButton.hidden = !hasMultipleImages;
      previousButton.disabled = false;
    }

    if (nextButton) {
      nextButton.hidden = !hasMultipleImages;
      nextButton.disabled = false;
    }
  }

  function selectColor(card, swatch) {
    const figure = card.querySelector(SELECTORS.slider);
    if (!figure) return;

    const color = getSwatchColor(swatch);
    if (!color || !getGroupSlides(figure, color).length) return;

    figure.dataset.activeColor = color;
    setSlide(figure, 0, { animate: false, wrap: true });
    preloadGroup(figure, color);

    const variantUrl = swatch.dataset.href;
    if (variantUrl) {
      const imageLink = figure.querySelector(SELECTORS.imageLink);
      const productLink = card.querySelector(SELECTORS.productLink);

      if (imageLink) imageLink.href = variantUrl;
      if (productLink) productLink.href = variantUrl;
    }
  }

  function initialiseSlider(figure) {
    if (!figure || figure.dataset.colorSliderInitialised === 'true') return;

    prepareTracks(figure);

    let color = figure.dataset.activeColor;
    if (!getGroupSlides(figure, color).length) {
      const firstSlide = figure.querySelector(SELECTORS.slide);
      color = firstSlide ? firstSlide.dataset.colorGroup : '';
      figure.dataset.activeColor = color;
    }

    setSlide(figure, 0, { animate: false, wrap: true });

    const card = figure.closest('product-card');
    if (card && color) {
      card.querySelectorAll(SELECTORS.swatch).forEach(function (swatch) {
        swatch.classList.toggle('active', getSwatchColor(swatch) === color);
      });
    }

    figure.dataset.colorSliderInitialised = 'true';

    if (sliderPreloadObserver) {
      sliderPreloadObserver.observe(figure);
    } else {
      preloadGroup(figure, color);
    }
  }

  const sliderPreloadObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          const figure = entry.target;
          preloadGroup(figure, figure.dataset.activeColor);
          observer.unobserve(figure);
        });
      }, {
        rootMargin: '500px 0px'
      })
    : null;

  function initialiseAll(root) {
    const scope = root || document;

    if (scope.matches && scope.matches(SELECTORS.slider)) {
      initialiseSlider(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(SELECTORS.slider).forEach(initialiseSlider);
    }
  }

  document.addEventListener('click', function (event) {
    const previous = event.target.closest(SELECTORS.previous);
    const next = event.target.closest(SELECTORS.next);
    if (!previous && !next) return;

    const figure = event.target.closest(SELECTORS.slider);
    if (!figure) return;
    if (figure.dataset.colorSliderLooping === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    const currentIndex = Number(figure.dataset.activeIndex || 0);
    setSlide(figure, next ? currentIndex + 1 : currentIndex - 1, {
      animate: true,
      wrap: true
    });
  });

  /*
   * Capture phase is intentional. Prestige's swatch click handler uses
   * stopImmediatePropagation(), so a normal delegated listener would not run.
   */
  document.addEventListener('click', function (event) {
    const swatch = event.target.closest(SELECTORS.swatch);
    if (!swatch) return;

    const card = swatch.closest('product-card');
    if (card) selectColor(card, swatch);
  }, true);

  document.addEventListener('mouseover', function (event) {
    const swatch = event.target.closest(SELECTORS.swatch);
    if (!swatch || event.relatedTarget && swatch.contains(event.relatedTarget)) return;

    const card = swatch.closest('product-card');
    if (card) selectColor(card, swatch);
  }, true);

  document.addEventListener('touchstart', function (event) {
    const figure = event.target.closest(SELECTORS.slider);
    if (!figure || !event.touches.length) return;
    if (figure.dataset.colorSliderLooping === 'true') return;

    const color = figure.dataset.activeColor;
    const slides = getGroupSlides(figure, color);
    if (slides.length <= 1) return;

    const touch = event.touches[0];
    const index = Number(figure.dataset.activeIndex || 0);
    const track = getTrack(figure, color);

    dragStates.set(figure, {
      startX: touch.clientX,
      startY: touch.clientY,
      startedAt: Date.now(),
      index: index,
      width: figure.getBoundingClientRect().width || 1,
      direction: null,
      distanceX: 0,
      track: track,
      slideCount: slides.length
    });

    if (track) track.style.transition = 'none';
    preloadNearbySlides(figure, color, index);
  }, { passive: true });

  document.addEventListener('touchmove', function (event) {
    const figure = event.target.closest(SELECTORS.slider);
    const state = figure && dragStates.get(figure);
    if (!figure || !state || !event.touches.length) return;

    const touch = event.touches[0];
    let distanceX = touch.clientX - state.startX;
    const distanceY = touch.clientY - state.startY;

    if (!state.direction && (Math.abs(distanceX) > 7 || Math.abs(distanceY) > 7)) {
      state.direction = Math.abs(distanceX) > Math.abs(distanceY) * 1.1
        ? 'horizontal'
        : 'vertical';
    }

    if (state.direction !== 'horizontal') return;

    if (event.cancelable) event.preventDefault();

    state.distanceX = distanceX;
    figure.classList.add('is-dragging');
    positionTrack(state.track, state.index, false, distanceX);
  }, { passive: false });

  function finishDrag(event, cancelled) {
    const figure = event.target.closest(SELECTORS.slider);
    const state = figure && dragStates.get(figure);
    if (!figure || !state) return;

    dragStates.delete(figure);

    if (cancelled || state.direction !== 'horizontal') {
      setSlide(figure, state.index, { animate: true, wrap: true });
      return;
    }

    const elapsed = Math.max(Date.now() - state.startedAt, 1);
    const velocity = Math.abs(state.distanceX) / elapsed;
    const crossedDistance = Math.abs(state.distanceX) > state.width * 0.18;
    const quickFlick = velocity > 0.45 && Math.abs(state.distanceX) > 24;
    let targetIndex = state.index;

    if (crossedDistance || quickFlick) {
      targetIndex += state.distanceX < 0 ? 1 : -1;
    }

    setSlide(figure, targetIndex, { animate: true, wrap: true });
    figure.dataset.suppressLinkUntil = String(Date.now() + 500);
  }

  document.addEventListener('touchend', function (event) {
    finishDrag(event, false);
  }, { passive: true });

  document.addEventListener('touchcancel', function (event) {
    finishDrag(event, true);
  }, { passive: true });

  document.addEventListener('click', function (event) {
    const link = event.target.closest(SELECTORS.imageLink);
    if (!link) return;

    const figure = link.closest(SELECTORS.slider);
    const suppressUntil = Number(figure && figure.dataset.suppressLinkUntil || 0);

    if (Date.now() < suppressUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    initialiseAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initialiseAll(event.target);
  });

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) initialiseAll(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  initialiseAll(document);
})();
